<template>
  <InterviewPageModal v-if="mediaViewerUrl">
    <MediaLayout :can-continue="true" @continue="mediaViewerUrl = undefined">
      <MediaImageViewer :url="mediaViewerUrl" />
      <template #button>
        {{ c('back_to_question') }}
      </template>
    </MediaLayout>
  </InterviewPageModal>
  <QuestionPadding v-else class="image-choice-question">
    <ScrollIntersectionObserver>
      <SubjectImage
        v-if="question.subjectImage"
        :url="question.subjectImage"
        :label="question.subject ?? ''"
      />
      <HintText>{{ hintText }}</HintText>
      <ImageSelect
        :display="display"
        :options="imageOptions"
        :selected-ids="selectedIds"
        :is-tick-shown="isTickShown"
        class="image-choice-question__answers"
        @select="selectId"
        @deselect="deselectId"
        @view-media="url => (mediaViewerUrl = url)"
      />
    </ScrollIntersectionObserver>
  </QuestionPadding>
</template>

<script lang="ts">
import { computed, type DeepReadonly, defineComponent, type PropType, ref } from 'vue'

import { type Answer } from '../../../answer/models/Answer'
import MediaImageViewer from '../../../core/components/dumb/MediaImageViewer.vue'
import MediaLayout from '../../../core/components/dumb/MediaLayout.vue'
import InterviewPageModal from '../../../interview/components/dumb/InterviewPageOverlay.vue'
import { c } from '../../../intl'
import HintText from '../../../question/components/dumb/HintText.vue'
import QuestionPadding from '../../../question/components/dumb/QuestionPadding.vue'
import SubjectImage from '../../../question/components/dumb/SubjectImage.vue'
import { assertFieldType } from '../../../question/models/Field'
import { type Question } from '../../../question/models/Question'
import ScrollIntersectionObserver from '../../../shared/components/dumb/ScrollIntersectionObserver.vue'
import ImageSelect, { type ImageOption } from '../dumb/ImageSelect.vue'

export default defineComponent({
  components: {
    ImageSelect,
    HintText,
    MediaImageViewer,
    MediaLayout,
    InterviewPageModal,
    ScrollIntersectionObserver,
    QuestionPadding,
    SubjectImage,
  },
  props: {
    question: {
      type: Object as PropType<DeepReadonly<Omit<Question<'imageChoice'>, 'id'>>>,
      required: true,
    },
    answers: {
      type: Array as PropType<DeepReadonly<Answer[]>>,
      required: true,
    },
  },
  emits: {
    answer: (_answer: { fieldId: string; value: { selected: boolean } }) => true,
  },
  setup(props, ctx) {
    const mediaViewerUrl = ref<string | undefined>(undefined)

    const imageChoiceFields = computed(() =>
      props.question.fields.filter(field => field.type === 'imageChoice'),
    )

    const selectedIds = computed(() =>
      props.answers
        .filter(answer => answer.type === 'choice' && answer.value.selected)
        .map(({ fieldId }) => fieldId),
    )

    return {
      imageOptions: computed((): ImageOption[] =>
        imageChoiceFields.value.map(field => {
          assertFieldType(field.type, 'imageChoice')
          return {
            id: field.id,
            description: field.description,
            url: field.url,
          }
        }),
      ),
      display: computed(() => props.question.display),
      hintText: computed(
        () =>
          ({
            single: c('single_choice.select_image'),
            multiple: c('multiple_choice.select_image'),
            max: c('multiple_choice.image_answer_limit', {
              quantity: props.question.maxAnswers,
              placeholders: {
                numImages: `${props.question.maxAnswers}`,
              },
            }),
          })[
            props.question.maxAnswers === 1
              ? 'single'
              : props.question.maxAnswers === Number.POSITIVE_INFINITY
                ? 'multiple'
                : 'max'
          ],
      ),
      isTickShown: computed(() => props.question.maxAnswers > 1),
      selectedIds,
      selectId: {
        single: (id: string) => {
          for (const selectedId of selectedIds.value)
            ctx.emit('answer', { fieldId: selectedId, value: { selected: false } })

          ctx.emit('answer', { fieldId: id, value: { selected: true } })
        },
        multiple: (id: string) => {
          if (selectedIds.value.length >= props.question.maxAnswers) return
          ctx.emit('answer', { fieldId: id, value: { selected: true } })
        },
      }[props.question.maxAnswers > 1 ? 'multiple' : 'single'],
      deselectId: (id: string): void => {
        ctx.emit('answer', { fieldId: id, value: { selected: false } })
      },
      mediaViewerUrl,
      c,
    }
  },
})
</script>

<style lang="postcss">
.image-choice-question {
  grid-template-rows: 1fr;
}
</style>
